<template>
  <span><!-- placeholder --></span>
</template>

<script setup>
/**
 * Note: Forte.js from vendor is meant to be included as <script src=...> in an HTML page. It is not "exported."
 * So, it needs to be "loaded" as it cannot be "imported" via JavaScript directly.
 *
 * Also, the Forte library will NOT load in this component. Thus, it is preloaded in its parent (Payment).
 */
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import getForteConfig from './forteConfig';

defineOptions({
  name: 'OneTimeToken',
  // The 'parent' property is non-standard and removed in setup script context.
});

const props = defineProps({
  cardNumber: {
    type: String,
    required: true,
  },
  cardMonth: {
    type: String,
    required: true,
  },
  cardYear: {
    type: String,
    required: true,
  },
  cardCvv: {
    type: String,
    required: true,
  },
  fetchToken: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['doPay']);

const store = useStore();

// Computed properties
const reservationSlotId = computed(() => store.state.user.reservationSlotId);

// Methods
const setLoading = (loading) => {
  store.dispatch('user/setStoreProperty', {
    property: 'isLoading',
    value: loading
  });
};

/**
 * Get OTT from Forte. (But it does not necessarily mean the OTT is good for payment, e.g. Forte still
 * return OTT with success status even if user inputs an unsupported credit card.)
 * @param res
 * @returns {Promise<void>}
 */
const ottSuccess = (res) => {
  setLoading(false);
  emit('doPay', {
    hasOtt: true,
    forte: res,
  });
};

/**
 * Cannot get OTT.
 * @param res
 */
const ottError = (res) => {
  setLoading(false);
  emit('doPay', {
    hasOtt: false,
    forte: res,
  });
};

/**
 * Get one time token from Forte based on credit card info.
 */
const getOtt = async () => {
  setLoading(true);

  // credit card only
  const data = {
    api_login_id: getForteConfig().key,
    card_number: props.cardNumber,
    expire_month: props.cardMonth,
    expire_year: props.cardYear,
    cvv: props.cardCvv,
  };

  // forte doesn't have a .finally event
  // The 'forte' global object is expected to be available from the script tag inclusion in the HTML page.
  // eslint-disable-next-line
  forte.createToken(data)
    .success(ottSuccess)
    .error(ottError);
};

// Watchers
watch(() => props.fetchToken, (newValue) => {
  if (newValue && reservationSlotId.value > 0) {
    getOtt();
  }
});
</script>